import { Product } from "../pos"
import { ProductChoice } from "./evaluated-service-bike-part"
import { ServiceProposalPartAction } from "./fresh-service-proposal"
import { PartGrade } from "./part-grade"
import { ServiceAction, ServicePartActionUtils } from "./service-part-action"

const choiceAlternatives = (choice: ProductChoice): Product[] => [
  choice.recommendedAlternative,
  ...choice.otherAlternatives,
]

/** Whichever alternative the customer picks, it has to be ordered. */
export const choiceAlwaysNeedsOrdering = (choice: ProductChoice): boolean =>
  choiceAlternatives(choice).every((product) => product.needsOrdering)

/**
 * Some alternatives are on the shelf and some are not, so whether anything gets
 * ordered comes down to which one the customer picks.
 */
export const choiceMayNeedOrdering = (choice: ProductChoice): boolean => {
  const alternatives = choiceAlternatives(choice)
  return (
    alternatives.some((product) => product.needsOrdering) &&
    !alternatives.every((product) => product.needsOrdering)
  )
}

interface ActionLike {
  products: Product[]
  productChoices: ProductChoice[]
}

/**
 * Whether anything on the action has to be ordered in — certainly, or depending
 * on which alternative the customer picks. Either way it is what moves the
 * delivery date, so the customer wants to see it.
 */
export const actionNeedsOrdering = (action: ActionLike): boolean =>
  action.products.some((product) => product.needsOrdering) ||
  action.productChoices.some(
    (choice) =>
      choiceAlwaysNeedsOrdering(choice) || choiceMayNeedOrdering(choice),
  )

const hasComment = (action: ServiceProposalPartAction): boolean =>
  action.comment !== null && action.comment.trim() !== ""

/**
 * Lower sorts first.
 *
 * 0 — the customer has something to do: a product to pick, or a part that has
 *     to be ordered in and moves the date they get the bike back.
 * 1 — the mechanic put something there to read: products, or a comment.
 * 2 — everything else.
 *
 * Judged on what the action *has* rather than on what is still unanswered: a
 * bucket that emptied as the customer answered its choices would reorder the
 * list under their finger mid-tap.
 */
const getPriority = (action: ServiceProposalPartAction): number => {
  if (action.productChoices.length > 0 || actionNeedsOrdering(action)) return 0
  if (action.products.length > 0 || hasComment(action)) return 1
  return 2
}

/**
 * Worse wear sorts first. "Montera" puts a component on the bike that is not
 * there yet, so it has no wear to report and nothing to say about urgency, and
 * an action that needs no grade may have none either: they rank past the worst real grade rather than being read as pristine. A finite
 * sentinel, so two of them subtract to 0 and fall through to the name instead
 * of handing the comparator a `NaN`.
 */
const NO_GRADE_RANK = PartGrade.TEN + 1

const getGradeRank = (action: ServiceProposalPartAction): number =>
  ServicePartActionUtils.addsNewComponent(action.action)
    ? NO_GRADE_RANK
    : (action.partGrade ?? NO_GRADE_RANK)

/**
 * The order a package's "Åtgärder" list is read in. It follows the spirit of
 * `sortBikeConditionParts` rather than reusing it: inside a package nothing is
 * declined and nothing is being skipped, which is most of what that sort ranks
 * on. What carries over is that a mechanic's comment pulls a part forward, and
 * the Swedish collation on the name so the rest is deterministic.
 *
 * "Justera" sinks within its bucket rather than to the bottom of the list. An
 * adjustment with an unanswered product choice still blocks the approve button,
 * and burying it under everything else would work against the bucket above it.
 */
export const sortProposalPartActions = (
  actions: ServiceProposalPartAction[],
): ServiceProposalPartAction[] =>
  [...actions].sort((a, b) => {
    const priorityDiff = getPriority(a) - getPriority(b)
    if (priorityDiff !== 0) return priorityDiff

    const aIsAdjust = a.action === ServiceAction.ADJUST
    const bIsAdjust = b.action === ServiceAction.ADJUST
    if (aIsAdjust !== bIsAdjust) return aIsAdjust ? 1 : -1

    const gradeDiff = getGradeRank(a) - getGradeRank(b)
    if (gradeDiff !== 0) return gradeDiff

    return a.partName.localeCompare(b.partName, "sv")
  })
